import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target,
  Zap,
  Send,
  Globe,
  FileText,
  Activity,
  Users,
  Presentation,
  Calculator,
  BarChart,
  HelpCircle,
  Search,
  X,
  Command,
  FileSignature,
  Home,
  Settings
} from "lucide-react";

// Available commands with metadata
const COMMANDS = [
  { cmd: 'command center', label: 'Command Center', icon: Home, path: '/command-center', description: 'Main dashboard', category: 'Navigate' },
  { cmd: 'new deal', label: 'New Acquisition', icon: Target, path: '/deals', description: 'Add a new property to your pipeline', category: 'Actions' },
  { cmd: 'run analysis', label: 'Run Analysis', icon: Zap, path: null, action: 'analysis', description: 'Analyze your latest transaction', category: 'Actions' },
  { cmd: 'execute offer', label: 'Execute Offer', icon: Send, path: '/contracts', description: 'Create and send contracts', category: 'Actions' },
  { cmd: 'market scan', label: 'Active Transactions', icon: Globe, path: '/deals', description: 'View all transactions', category: 'Navigate' },
  { cmd: 'capital model', label: 'Capital Model', icon: Calculator, path: '/calculator', description: 'Run financial scenarios', category: 'Navigate' },
  { cmd: 'execution flow', label: 'Execution Flow', icon: Activity, path: '/execution-flow', description: 'View pipeline stages', category: 'Navigate' },
  { cmd: 'network', label: 'Partner Network', icon: Users, path: '/network', description: 'Manage execution partners', category: 'Navigate' },
  { cmd: 'deck generator', label: 'Deck Generator', icon: Presentation, path: '/deal-packets', description: 'Create pitch decks', category: 'Navigate' },
  { cmd: 'contracts', label: 'Contracts', icon: FileSignature, path: '/contracts', description: 'Manage contracts & e-signatures', category: 'Navigate' },
  { cmd: 'underwriting', label: 'Underwriting', icon: FileText, path: '/underwriting', description: 'AI-powered deal analysis', category: 'Navigate' },
  { cmd: 'performance', label: 'Performance', icon: BarChart, path: '/performance', description: 'View metrics dashboard', category: 'Navigate' },
  { cmd: 'upgrade', label: 'Upgrade Plan', icon: Settings, path: '/upgrade', description: 'Manage subscription', category: 'Settings' },
  { cmd: 'help', label: 'Help', icon: HelpCircle, path: null, action: 'help', description: 'Show available commands', category: 'Help' },
];

export default function GlobalCommandPalette({ isOpen, onClose, deals = [] }) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredCommands, setFilteredCommands] = useState(COMMANDS);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setFilteredCommands(COMMANDS);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setFilteredCommands(COMMANDS);
      setSelectedIndex(0);
      return;
    }

    const q = query.toLowerCase();
    const matches = COMMANDS.filter(c =>
      c.cmd.includes(q) ||
      c.label.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      c.category.toLowerCase().includes(q)
    );
    setFilteredCommands(matches);
    setSelectedIndex(0);
  }, [query]);

  const executeCommand = (cmd) => {
    if (cmd.action === 'help') {
      toast.info("Use ⌘K to open command palette. Type to search commands.");
    } else if (cmd.action === 'analysis') {
      if (deals.length > 0) {
        toast.success("Opening analysis for latest transaction...");
        navigate(`/deals/${deals[0].deal_id}`);
      } else {
        toast.info("No transactions to analyze. Add a new acquisition first.");
        navigate('/deals');
      }
    } else if (cmd.path) {
      toast.success(`Opening ${cmd.label}...`);
      navigate(cmd.path);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < filteredCommands.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev > 0 ? prev - 1 : filteredCommands.length - 1
      );
    } else if (e.key === 'Enter' && filteredCommands.length > 0) {
      e.preventDefault();
      executeCommand(filteredCommands[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Group commands by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  let flatIndex = 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Palette */}
      <div className="relative w-full max-w-xl bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#2E3440] rounded-2xl shadow-2xl overflow-hidden">
        {/* Search Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-white/[0.06]">
          <Search className="w-5 h-5 text-white/50" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent text-white text-base placeholder:text-white/40 focus:outline-none"
          />
          <div className="flex items-center gap-1.5 px-2 py-1 bg-white/[0.02] rounded border border-white/[0.06]">
            <span className="text-[10px] text-white/40">ESC</span>
          </div>
        </div>

        {/* Commands List */}
        <div className="max-h-[400px] overflow-y-auto">
          {Object.entries(groupedCommands).map(([category, commands]) => (
            <div key={category}>
              <div className="px-4 py-2 text-[10px] font-semibold text-white/40 uppercase tracking-wider bg-white/[0.01]">
                {category}
              </div>
              {commands.map((cmd) => {
                const currentIndex = flatIndex++;
                const Icon = cmd.icon;
                const isSelected = currentIndex === selectedIndex;
                
                return (
                  <button
                    key={cmd.cmd}
                    onClick={() => executeCommand(cmd)}
                    className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-all ${
                      isSelected 
                        ? 'bg-[#D4AF37]/10' 
                        : 'hover:bg-white/[0.02]'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-[#D4AF37]/20' : 'bg-white/[0.02]'
                    }`}>
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-[#D4AF37]' : 'text-white/50'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-white/70'}`}>
                        {cmd.label}
                      </div>
                      <div className="text-[11px] text-white/40 truncate">{cmd.description}</div>
                    </div>
                    {isSelected && (
                      <div className="text-[10px] text-white/40 font-mono">↵ Enter</div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          
          {filteredCommands.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Search className="w-8 h-8 text-white/30 mx-auto mb-2" />
              <p className="text-white/40 text-sm">No commands found</p>
              <p className="text-white/30 text-xs mt-1">Try a different search term</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-4 text-[10px] text-white/40">
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-white/[0.02] rounded text-white/50">↑↓</span>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-white/[0.02] rounded text-white/50">↵</span>
              Select
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-white/[0.02] rounded text-white/50">ESC</span>
              Close
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-white/30">
            <Command className="w-3 h-3" />
            <span>DealForge</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export { COMMANDS };
